import React, { useState, useEffect, useRef } from 'react';

// 자동 회전 슬라이더 (파노라마 형태).
// 일정 시간마다 다음 이미지로 페이드 전환 + 좌/우 화살표 + 하단 점 인디케이터.
// 마우스 hover 시 일시정지. images 1장이면 컨트롤 숨김.
//
// Props:
//   images: [{src, alt}] (1+ 권장)
//   intervalMs: 자동 전환 간격 (기본 4500)
//   aspectRatio: CSS aspect-ratio (기본 '16 / 9')
//   onClick: (index) => void — 이미지 클릭 핸들러 (예: lightbox 열기, 비교 모달 열기)

export default function PanoramaSlider({
  images,
  intervalMs = 4500,
  aspectRatio = '16 / 9',
  onClick,
  // showThumbnails: when true, render a single-row thumbnail strip below the
  // main slide (click to jump). Replaces the dot indicator. Opt-in (default off)
  // so existing consumers (Gallery, BeforeAfter) keep the dot UI unchanged.
  showThumbnails = false,
  // showBadge: when true, overlay the active slide's `badge` text in the top-left
  // corner (e.g. '작업 전' / '작업 후'). Opt-in (default off) so existing
  // consumers (Gallery) are unchanged.
  showBadge = false,
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const total = images?.length || 0;

  useEffect(() => {
    if (total <= 1 || paused) return undefined;
    timerRef.current = setInterval(() => {
      setIndex(i => (i + 1) % total);
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [total, paused, intervalMs]);

  if (total === 0) return null;

  const go = (delta) => setIndex(i => (i + delta + total) % total);

  return (
    <div>
    <div
      style={{
        position: 'relative', width: '100%', aspectRatio,
        background: 'var(--surface)', borderRadius: 12, overflow: 'hidden',
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {images.map((img, i) => (
        <button
          key={`${img.src}-${i}`}
          type="button"
          onClick={() => onClick && onClick(i)}
          aria-label={img.alt || `슬라이드 ${i + 1}`}
          style={{
            position: 'absolute', inset: 0,
            padding: 0, border: 'none', background: 'none',
            cursor: onClick ? 'pointer' : 'default',
            opacity: i === index ? 1 : 0,
            transition: 'opacity 0.6s ease',
            pointerEvents: i === index ? 'auto' : 'none',
          }}
        >
          <img
            src={img.src}
            alt={img.alt || ''}
            loading={i === 0 ? 'eager' : 'lazy'}
            style={{
              width: '100%', height: '100%',
              objectFit: 'cover', display: 'block',
            }}
          />
        </button>
      ))}

      {/* Active-slide badge (e.g. 작업 전 / 작업 후). */}
      {showBadge && images[index]?.badge && (
        <span style={{
          position: 'absolute', top: 12, left: 12, zIndex: 2,
          padding: '4px 12px', borderRadius: 999,
          background: 'rgba(0,0,0,0.6)', color: '#fff',
          fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.02em',
          pointerEvents: 'none',
        }}>
          {images[index].badge}
        </span>
      )}

      {total > 1 && (
        <>
          <ArrowButton dir="left" onClick={() => go(-1)} />
          <ArrowButton dir="right" onClick={() => go(1)} />
          {/* Dot indicator only when the thumbnail strip is not shown. */}
          {!showThumbnails && (
            <div style={{
              position: 'absolute', bottom: 12, left: 0, right: 0,
              display: 'flex', justifyContent: 'center', gap: 6,
              pointerEvents: 'none',
            }}>
              {images.map((_, i) => (
                <span key={i} style={{
                  width: i === index ? 18 : 8, height: 8, borderRadius: 4,
                  background: i === index ? 'var(--primary)' : 'rgba(255,255,255,0.5)',
                  transition: 'width 0.3s, background 0.3s',
                }} />
              ))}
            </div>
          )}
        </>
      )}
    </div>

    {/* Single-row thumbnail strip below the main slide (opt-in). */}
    {showThumbnails && total > 1 && (
      <div style={{
        display: 'flex', gap: 8, marginTop: 8,
        overflowX: 'auto', paddingBottom: 4,
      }}>
        {images.map((img, i) => (
          <button
            key={`thumb-${img.src}-${i}`}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`${i + 1}번 사진 보기`}
            style={{
              flex: '0 0 auto', width: 72, aspectRatio: '4 / 3',
              padding: 0, borderRadius: 6, overflow: 'hidden', cursor: 'pointer',
              border: i === index ? '2px solid var(--primary)' : '2px solid transparent',
              opacity: i === index ? 1 : 0.6,
              transition: 'opacity 0.2s, border-color 0.2s',
              background: 'none',
            }}
          >
            <img
              src={img.src}
              alt={img.alt || ''}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </button>
        ))}
      </div>
    )}
    </div>
  );
}

function ArrowButton({ dir, onClick }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      aria-label={dir === 'left' ? '이전' : '다음'}
      style={{
        position: 'absolute', top: '50%', transform: 'translateY(-50%)',
        [dir]: 12,
        width: 40, height: 40, borderRadius: '50%',
        background: 'rgba(0,0,0,0.45)', color: '#fff',
        border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1.4rem', lineHeight: 1,
      }}
    >
      {dir === 'left' ? '‹' : '›'}
    </button>
  );
}
